import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ChevronDown, ClipboardList, Eye, Loader2, RefreshCw, Scissors, Sparkles, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtNum } from "@/lib/format";
import { btnGhost } from "@/components/app/common";

const AREA = {
  investire: { icon: TrendingUp, label: "Investire", cls: "bg-[#8AA090]/15 text-[#4E6656]" },
  tagliare: { icon: Scissors, label: "Tagliare", cls: "bg-[#E07A5F]/12 text-[#A2513C]" },
  monitorare: { icon: Eye, label: "Monitorare", cls: "bg-[#CBA368]/18 text-[#8A6A33]" },
};
const PRIO = { alta: "bg-[#C0573F] text-white", media: "bg-[#E8C5C8] text-[#77594E]", bassa: "bg-[#EAE3DC] text-[#5E4A42]" };
const fmtFatto = (f) => (f.unita === "€" ? `${fmtNum(f.valore)} €` : f.unita === "%" ? `${fmtNum(f.valore, 1)}%` : `${fmtNum(f.valore, 0)}${f.unita === "pz" ? " pz" : ""}`);

export function useInsights(anno, mese) {
  const [state, setState] = useState({ loading: true });
  const timer = useRef();
  const params = mese ? { anno, mese } : { anno };

  const poll = useCallback(async (autoGen) => {
    try {
      const { data } = await api.get("/ai/insights", { params: mese ? { anno, mese } : { anno } });
      // missing or stale (data changed since generation): regenerate automatically
      const rigenera = autoGen && (data.stato === "assente" || data.stato === "obsoleto");
      if (rigenera) await api.post("/ai/insights", null, { params: mese ? { anno, mese } : { anno } });
      if (data.stato === "in_corso" || rigenera) {
        setState({ loading: true, data, aggiornamento: data.stato === "obsoleto" });
        timer.current = setTimeout(() => poll(false), 3000);
        return;
      }
      setState({ loading: false, data, error: data.stato === "errore" ? data.errore : null });
    } catch (e) {
      setState({ loading: false, error: errMsg(e) });
    }
  }, [anno, mese]);

  useEffect(() => {
    setState({ loading: true });
    poll(true);
    return () => clearTimeout(timer.current);
  }, [poll]);

  const regenerate = async () => {
    clearTimeout(timer.current);
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await api.post("/ai/insights", null, { params });
      timer.current = setTimeout(() => poll(false), 3000);
    } catch (e) {
      setState({ loading: false, error: errMsg(e) });
    }
  };
  return { ...state, regenerate };
}

function DatiUsati({ fonti, i }) {
  const [open, setOpen] = useState(false);
  if (!fonti?.length) return null;
  return (
    <div className="mt-3 text-xs">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`insight-dati-toggle-${i}`} className="inline-flex items-center gap-1 text-[#8C6D62] font-semibold hover:underline">
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />Dati usati ({fonti.length})
      </button>
      {open && (
        <ul className="mt-2 space-y-1 rounded-xl border border-[#EAE3DC] p-2.5" data-testid={`insight-dati-${i}`}>
          {fonti.map((f) => (
            <li key={f.id} className="flex justify-between gap-3" data-testid={`insight-fonte-${i}-${f.id}`}>
              <Link to={f.link} className="text-[#5E4A42] hover:text-[#8C6D62] hover:underline">{f.etichetta}</Link>
              <b className="tabular whitespace-nowrap">{fmtFatto(f)}</b>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const InsightItem = ({ it, i, compact }) => {
  const a = AREA[it.area] || AREA.monitorare;
  return (
    <article data-testid={`insight-item-${i}`} className="rounded-2xl border border-[#EAE3DC] bg-white p-5 fade-up" style={{ animationDelay: `${i * 70}ms` }}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.cls}`}><a.icon className="h-3 w-3" />{a.label}</span>
        <span data-testid={`insight-priorita-${i}`} className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${PRIO[it.priorita]}`}>Priorità {it.priorita}</span>
      </div>
      <h3 className="font-display text-lg leading-snug mb-2">{it.titolo}</h3>
      <p className={`text-sm text-[#5E4A42] leading-relaxed ${compact ? "line-clamp-3" : ""}`} data-testid={`insight-spiegazione-${i}`}>{it.spiegazione}</p>
      <div className="mt-3 text-sm rounded-xl bg-[#FAF7F5] px-3 py-2.5">
        <span className="font-semibold text-[#8C6D62]">Azione consigliata: </span>
        <span className={compact ? "line-clamp-2" : ""}>{it.azione}</span>
      </div>
      {!compact && <DatiUsati fonti={it.fonti} i={i} />}
    </article>
  );
};

export const DatiInsufficienti = ({ d, label }) => d?.mancanti?.length ? (
  <div data-testid="insights-dati-insufficienti" className="rounded-2xl border border-[#CBA368]/50 bg-[#CBA368]/10 p-5 mb-5">
    <h3 className="font-medium flex items-center gap-2 text-[#8A6A33]"><ClipboardList className="h-4 w-4" />Dati insufficienti per consigli di investimento — {label}</h3>
    <p className="text-xs text-[#756B64] mt-1">Per un'analisi affidabile completa questi dati:</p>
    <ul className="mt-2 text-sm list-disc pl-5 space-y-1 text-[#5E4A42]">{d.mancanti.map((m) => <li key={m}>{m}</li>)}</ul>
  </div>
) : null;

export const InsightsMeta = ({ data }) => data?.generated_at ? (
  <p className="text-xs text-[#756B64] mb-4" data-testid="insights-generated-at">
    Periodo analizzato: <b data-testid="insights-periodo-label">{data.periodo_label || data.periodo}</b> · generati il {fmtDate(data.generated_at)} alle {new Date(data.generated_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} sui dati di quel momento; si aggiornano da soli quando i dati cambiano. Ogni numero è verificato sui dati dell'app{data.scartati ? ` (${data.scartati} consigli scartati perché non verificabili)` : ""}.
  </p>
) : null;

export const InsightsState = ({ s, rows = 3 }) => {
  if (s.loading)
    return (
      <div data-testid="insights-loading" className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-[#756B64]"><Loader2 className="h-4 w-4 animate-spin" />{s.aggiornamento ? "I dati sono cambiati: aggiorno i consigli…" : "Il consulente sta analizzando i tuoi dati…"} (circa 30-60 secondi)</div>
        {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-[#F4EFEA] animate-pulse" />)}
      </div>
    );
  if (s.error)
    return (
      <div data-testid="insights-error" className="flex items-start gap-3 rounded-2xl bg-[#E07A5F]/10 text-[#A2513C] px-4 py-3 text-sm">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{s.error} Premi "Rigenera" per riprovare.</span>
      </div>
    );
  return null;
};

export function InsightsCard({ anno, mese }) {
  const s = useInsights(anno, mese);
  const items = s.data?.insights || [];
  return (
    <section data-testid="insights-card" className="card-soft p-5 sm:p-7 fade-up">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-[#E8C5C8]/50 grid place-items-center"><Sparkles className="h-5 w-5 text-[#8C6D62]" /></span>
          <div>
            <h2 className="text-lg font-medium">Consigli automatici</h2>
            <p className="text-xs text-[#756B64]" data-testid="insights-card-periodo">Analisi AI dei dati di {s.data?.periodo_label || `${mese}/${anno}`}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className={`${btnGhost} h-9`} onClick={s.regenerate} disabled={s.loading} data-testid="insights-regenerate-button"><RefreshCw className="h-3.5 w-3.5 mr-1" />Rigenera</Button>
          <Link to={`/consulente?anno=${anno}&mese=${mese}`} data-testid="insights-see-all" className={`${btnGhost} border h-9 inline-flex items-center px-4 text-sm`}>Vedi tutti</Link>
        </div>
      </div>
      <InsightsState s={s} />
      {!s.loading && <DatiInsufficienti d={s.data?.dati_insufficienti} label={s.data?.periodo_label} />}
      {!s.loading && items.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="insights-list">
          {items.slice(0, 3).map((it, i) => <InsightItem key={i} it={it} i={i} compact />)}
        </div>
      )}
    </section>
  );
}
